/**
 * Product Configurator JS – Live Preview for Custom Baby Height Chart
 * Optimized: event delegation, no polling, debounced resize, accessibility
 */

(function () {
  'use strict';

  /* ── Color palettes (moved to top for clarity) ── */
  const BG_COLORS = [
    { name: 'Cream', hex: '#F5F3EE', light: true },
    { name: 'Off-white', hex: '#F2EDE4', light: true },
    { name: 'Sage', hex: '#C7D5C6', light: true },
    { name: 'Moss', hex: '#BFCAB3', light: true },
    { name: 'Powder blue', hex: '#D7E8F7', light: true },
  ];

  const INK_COLORS = [
    { name: 'Kol', hex: '#2C2825', light: false },
    { name: 'Earthy green', hex: '#8EA88C', light: false },
    { name: 'Muted blue', hex: '#6A87A8', light: false },
    { name: 'Moss', hex: '#6A7D62', light: false },
    { name: 'Dusty rose', hex: '#B87878', light: false },
  ];

  /* ── State (single source of truth) ── */
  const state = {
    bg: BG_COLORS[0].hex,
    ink: INK_COLORS[0].hex,
    font: 'luxury',
    illu: 'hearts',
    skin: 'light',
    name: '',
    date: '',
    time: '',
    length: 51,
    weight: 3490,
    product: null,
  };

  /* ── Constants ── */
  const MONTHS_LONG = [
    'JANUARI', 'FEBRUARI', 'MARS', 'APRIL', 'MAJ', 'JUNI',
    'JULI', 'AUGUSTI', 'SEPTEMBER', 'OKTOBER', 'NOVEMBER', 'DECEMBER',
  ];
  const MONTHS_SHORT = ['JAN', 'FEB', 'MAR', 'APR', 'MAJ', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEC'];
  const DEFAULT_DATE = '01 JANUARI 2024';
  const DEFAULT_TIME = '03:42';
  const RESIZE_DEBOUNCE_MS = 150;
  const BASE_ILLU_HEIGHT = 210;
  const MOBILE_ILLU_HEIGHT = 160;
  const REFERENCE_LENGTH = 51;

  let resizeTimer = null;
  let cachedMobile = null;

  /* ── Utility: mobile breakpoint ── */
  function isMobile() {
    if (cachedMobile !== null) return cachedMobile;
    cachedMobile = window.innerWidth < 768;
    return cachedMobile;
  }

  /* ── Date formatting ── */
  function formatDateLong(dateStr) {
    if (!dateStr) return DEFAULT_DATE;
    const dt = new Date(dateStr + 'T12:00:00');
    if (isNaN(dt.getTime())) return DEFAULT_DATE;
    const day = String(dt.getDate()).padStart(2, '0');
    const month = MONTHS_LONG[dt.getMonth()] || '';
    return `${day} ${month} ${dt.getFullYear()}`;
  }

  function formatTime(timeStr) {
    return timeStr || DEFAULT_TIME;
  }

  /* ── Skin color ── */
  function getSkinColor() {
    return state.skin === 'dark' ? '#5a2d14' : state.ink;
  }

  /* ── Render ── */
  function render() {
    const len = state.length || REFERENCE_LENGTH;
    const wgt = state.weight
      ? Number(state.weight).toLocaleString('sv-SE')
      : '3 490';
    const n = state.name || '';
    const mobile = isMobile();

    /* Poster background & font */
    const poster = document.getElementById('poster');
    if (poster) {
      poster.style.backgroundColor = state.bg;
      poster.className = 'poster font-' + state.font;
    }

    /* Illustration height scales with length */
    const illuHeight = Math.round((len / REFERENCE_LENGTH) * (mobile ? MOBILE_ILLU_HEIGHT : BASE_ILLU_HEIGHT));
    const illustrationWrap = document.getElementById('illustrationWrap');
    if (illustrationWrap) {
      illustrationWrap.style.height = illuHeight + 'px';
    }

    /* Skin color on illustration paths */
    const skinColor = getSkinColor();
    document.querySelectorAll('#illuPaths path').forEach(function (p) {
      p.style.fill = skinColor;
    });

    /* Divider */
    const posterDivider = document.getElementById('posterDivider');
    if (posterDivider) posterDivider.style.backgroundColor = state.ink;

    /* Name – responsive sizing */
    const posterName = document.getElementById('posterName');
    if (posterName) {
      posterName.style.color = state.ink;
      posterName.textContent = n;
      let fs = '';
      if (n.length > 14) fs = '0.85rem';
      else if (n.length > 10) fs = '1.05rem';
      else if (n.length > 7) fs = '1.25rem';
      posterName.style.fontSize = fs;
    }

    /* Stats */
    const statsHTML =
      formatDateLong(state.date) +
      '<br>' +
      len +
      ' CM · ' +
      wgt +
      ' G<br>' +
      formatTime(state.time);

    const posterStats = document.getElementById('posterStats');
    if (posterStats) {
      posterStats.style.color = state.ink;
      posterStats.innerHTML = statsHTML;
    }

    /* Info panels */
    const posterInfoName = document.getElementById('posterInfoName');
    if (posterInfoName) posterInfoName.textContent = n;

    const posterInfoDetail = document.getElementById('posterInfoDetail');
    if (posterInfoDetail) posterInfoDetail.innerHTML = statsHTML;

    /* Illustration variant toggles */
    const illuPaths = document.getElementById('illuPaths');
    const randerPaths = document.getElementById('randerPaths');
    const heartsLayerH = document.getElementById('heartsLayerH');

    if (illuPaths) illuPaths.style.display = '';
    if (randerPaths) randerPaths.style.display = state.illu === 'stripes' ? '' : 'none';
    if (heartsLayerH) heartsLayerH.style.display = state.illu === 'hearts' ? '' : 'none';

    /* Scale notes */
    const scaleText = len + ' cm';
    document.getElementById('scaleNote').textContent = scaleText;
    const scaleNoteDesktop = document.getElementById('scaleNoteDesktop');
    if (scaleNoteDesktop) scaleNoteDesktop.textContent = scaleText;
  }

  /* ── Active state toggles for segment buttons ── */
  function setActive(id, condition) {
    const el = document.getElementById(id);
    if (el) el.classList.toggle('active', condition);
  }

  /* ── Swatch builder ── */
  function buildSwatches(containerId, colors, stateKey) {
    const wrap = document.getElementById(containerId);
    if (!wrap) return;

    /* Fragment for batch insert (performance) */
    const fragment = document.createDocumentFragment();
    const swatchEls = [];

    colors.forEach(function (c) {
      const el = document.createElement('button');
      el.className =
        'swatch' +
        (c.light ? ' light-swatch' : '') +
        (state[stateKey] === c.hex ? ' active' : '');
      el.style.backgroundColor = c.hex;
      el.title = c.name;
      el.dataset.color = c.hex;
      el.dataset.light = c.light ? '1' : '0';
      el.setAttribute('aria-label', c.name);
      el.setAttribute('aria-pressed', state[stateKey] === c.hex ? 'true' : 'false');
      el.setAttribute('type', 'button');
      fragment.appendChild(el);
      swatchEls.push({ el: el, hex: c.hex, light: c.light });
    });

    wrap.appendChild(fragment);

    /* Event delegation on container */
    wrap.addEventListener('click', function (e) {
      const btn = e.target.closest('.swatch');
      if (!btn || !wrap.contains(btn)) return;

      const hex = btn.dataset.color;
      const isLight = btn.dataset.light === '1';
      state[stateKey] = hex;

      /* Update active state */
      swatchEls.forEach(function (s) {
        const isActive = s.hex === hex;
        s.el.classList.toggle('active', isActive);
        s.el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      /* Background/ink interdependence */
      if (stateKey === 'bg') {
        const inkWrap = document.getElementById('inkSwatches');
        if (!inkWrap) { render(); return; }

        const inkSwatches = Array.from(inkWrap.querySelectorAll('.swatch'));
        if (!isLight) {
          state.ink = '#F5F0E8';
          inkSwatches.forEach(function (s, i) {
            const active = i === inkSwatches.length - 1;
            s.classList.toggle('active', active);
            s.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
        } else if (state.ink === '#F5F0E8') {
          state.ink = INK_COLORS[0].hex;
          inkSwatches.forEach(function (s, i) {
            const active = i === 0;
            s.classList.toggle('active', active);
            s.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
        }
      }
      render();
    });
  }

  /* ── Layout (visibility of desktop/mobile badges) ── */
  function applyLayout() {
    cachedMobile = null;
    const mobile = isMobile();
    const desktopBadge = document.getElementById('desktopBadge');
    const posterInfo = document.getElementById('posterInfoPanel');
    if (desktopBadge) desktopBadge.style.display = mobile ? 'none' : 'flex';
    if (posterInfo) posterInfo.style.display = mobile ? 'flex' : 'none';
  }

  /* ── Debounced resize ── */
  function onResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      applyLayout();
      render();
    }, RESIZE_DEBOUNCE_MS);
  }

  /* ── Input handlers (no inline handlers, entry points for data attributes) ── */
  function handleName(value) {
    state.name = value;
    const display = value || '';
    const posterName = document.getElementById('posterName');
    const posterInfoName = document.getElementById('posterInfoName');
    if (posterName) posterName.textContent = display;
    if (posterInfoName) posterInfoName.textContent = display;
  }

  function handleDate(value) {
    state.date = value;
    render();
  }

  function handleTime(value) {
    state.time = value;
    render();
  }

  function handleLength(value) {
    const n = parseInt(value, 10);
    state.length = !isNaN(n) && n >= 1 ? n : REFERENCE_LENGTH;
    render();
  }

  function handleWeight(value) {
    const n = parseInt(value, 10);
    state.weight = !isNaN(n) && n >= 1 ? n : 3490;
    render();
  }

  function setFont(f) {
    state.font = f;
    setActive('btnLuxury', f === 'luxury');
    setActive('btnPersonal', f === 'personal');
    render();
  }

  function setIllu(v) {
    state.illu = v;
    setActive('btnStripes', v === 'stripes');
    setActive('btnHearts', v === 'hearts');
    render();
  }

  function setSkin(s) {
    state.skin = s;
    setActive('btnSkinLight', s === 'light');
    setActive('btnSkinDark', s === 'dark');
    render();
  }

  /* ── Stub download handlers (preserve existing behavior) ── */
  function downloadPreview() {
    alert('Förhandsgranskning – denna funktion kan implementeras med html2canvas.');
  }

  function downloadPDF() {
    alert('PDF-export – koppla till html2canvas + jsPDF när klart.');
  }

  /* ── Init ── */
  function init(productData) {
    state.product = productData;

    buildSwatches('bgSwatches', BG_COLORS, 'bg');
    buildSwatches('inkSwatches', INK_COLORS, 'ink');

    /* Set initial input values */
    const lengthInput = document.getElementById('lengthInput');
    const weightInput = document.getElementById('weightInput');
    if (lengthInput) lengthInput.value = '51';
    if (weightInput) weightInput.value = '3490';

    applyLayout();
    render();

    window.addEventListener('resize', onResize, { passive: true });
  }

  /* ── Expose globals (backwards-compatible entry points) ── */
  window.MM_init = init;
  window.MM_name = handleName;
  window.MM_date = handleDate;
  window.MM_time = handleTime;
  window.MM_len = handleLength;
  window.MM_wgt = handleWeight;
  window.MM_setFont = setFont;
  window.MM_setIllu = setIllu;
  window.MM_setSkin = setSkin;
  window.MM_downloadPreview = downloadPreview;
  window.MM_downloadPDF = downloadPDF;
})();
